/**
 * Google Tag Manager dataLayer helpers.
 *
 * Pushes the same eecEvent / eecAction payloads as the legacy Vue shop so
 * existing GTM tags keep working.
 */

window.dataLayer = window.dataLayer || [];

const SESSION_ENDPOINT = '/datalayer/set-sessions';

/**
 * @returns {string}
 */
function csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
}

/**
 * @returns {boolean}
 */
function hasDataLayer() {
    return typeof window.dataLayer !== 'undefined';
}

/**
 * @param {object} payload
 * @returns {void}
 */
function pushEcommerce(payload) {
    if (!hasDataLayer()) {
        return;
    }

    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push(payload);
}

/**
 * Persist the current item list so later add-to-cart / purchase events
 * can attribute the items to this list.
 *
 * @param {string|null} listName
 * @param {string|null} listId
 * @returns {void}
 */
function persistItemList(listName, listId) {
    if (listName) {
        window.item_list_name = listName;
    }

    if (listId) {
        window.item_list_id = listId;
    }

    const token = csrfToken();

    if (!token) {
        return;
    }

    fetch(SESSION_ENDPOINT, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-CSRF-TOKEN': token,
            'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
            item_list_name: listName || null,
            item_list_id: listId || null,
        }),
    }).catch(() => {
        // Session persistence is best-effort; ecommerce pushes still fire.
    });
}

/**
 * @param {object} item
 * @returns {object}
 */
function withListContext(item) {
    const next = { ...item };

    if (!next.item_list_name && window.item_list_name) {
        next.item_list_name = String(window.item_list_name);
    }

    if (!next.item_list_id && window.item_list_id) {
        next.item_list_id = String(window.item_list_id);
    }

    return next;
}

/**
 * @returns {void}
 */
function loadStandardData() {
    if (!hasDataLayer()) {
        return;
    }

    const loggedIn = window.loggedIn === '1' || window.loggedIn === 1 || window.loggedIn === true;

    const data = {
        event: 'eecEvent',
        eecAction: 'loadStandardData',
        page: {
            type: window.pageTemplate || 'null',
            language: window.language || 'nl',
        },
        user: {
            loginStatus: loggedIn ? 'Logged in' : 'Logged out',
            consentsLevel: 'null',
            internalTraffic: 'false',
        },
    };

    if (loggedIn && window.user_data) {
        data.enhanced_conversion_data = {
            email: window.user_data.email,
            phone_number: window.user_data.phone,
            user_id: window.user_data.id,
        };

        const addresses = window.user_data.addresses || [];

        if (addresses.length) {
            const firstAddress = addresses[0];

            data.enhanced_conversion_data.address = {
                first_name: firstAddress.first_name,
                last_name: [firstAddress.middle_name, firstAddress.last_name].filter(Boolean).join(' ').trim(),
                street: [firstAddress.street, firstAddress.number, firstAddress.number_addition].filter(Boolean).join(' ').trim(),
                city: firstAddress.city,
                region: null,
                postal_code: firstAddress.zip,
                country: firstAddress.country?.name,
                country_iso: firstAddress.country?.code
                    ? String(firstAddress.country.code).toUpperCase()
                    : null,
            };
        }
    }

    window.dataLayer.push(data);
}

/**
 * @param {Array} items
 * @param {string|null} listName
 * @param {string|null} listId
 * @returns {void}
 */
function viewItemList(items, listName = null, listId = null) {
    if (!items || !items.length) {
        return;
    }

    persistItemList(listName, listId);

    pushEcommerce({
        event: 'eecEvent',
        eecAction: 'view_item_list',
        ecommerce: {
            items,
        },
    });
}

/**
 * @param {object} item
 * @returns {void}
 */
function selectItem(item) {
    const data = withListContext(item);

    pushEcommerce({
        event: 'eecEvent',
        eecAction: 'select_item',
        ecommerce: {
            currency: 'EUR',
            items: [data],
        },
    });
}

/**
 * @param {Array} items
 * @returns {void}
 */
function viewItem(items) {
    if (!items || !items.length) {
        return;
    }

    const totalValue = items.reduce((sum, item) => sum + (parseFloat(item.price) || 0), 0);

    pushEcommerce({
        event: 'eecEvent',
        eecAction: 'view_item',
        ecommerce: {
            currency: 'EUR',
            value: parseFloat(totalValue.toFixed(2)),
            items: items.map((item) => withListContext(item)),
        },
    });
}

/**
 * @param {object} item
 * @returns {void}
 */
function addToCart(item) {
    const data = withListContext(item);
    const quantity = parseInt(data.quantity, 10) || 1;
    const totalValue = (parseFloat(data.price) || 0) * quantity;

    pushEcommerce({
        event: 'eecEvent',
        eecAction: 'add_to_cart',
        ecommerce: {
            currency: 'EUR',
            value: parseFloat(totalValue.toFixed(2)),
            items: [data],
        },
    });
}

/**
 * @param {object} item
 * @returns {void}
 */
function removeFromCart(item) {
    pushEcommerce({
        event: 'eecEvent',
        eecAction: 'remove_from_cart',
        ecommerce: {
            items: [withListContext(item)],
        },
    });
}

/**
 * @param {object} payload
 * @returns {void}
 */
function viewCart(payload) {
    if (!payload || !payload.items || !payload.items.length) {
        return;
    }

    pushEcommerce({
        event: 'eecEvent',
        eecAction: 'view_cart',
        ecommerce: {
            currency: 'EUR',
            value: parseFloat(payload.value || 0),
            items: payload.items,
        },
    });
}

/**
 * @param {object} item
 * @returns {void}
 */
function addToWishlist(item) {
    pushEcommerce({
        event: 'eecEvent',
        eecAction: 'add_to_wishlist',
        ecommerce: {
            items: [withListContext(item)],
        },
    });
}

/**
 * @param {object} item
 * @returns {void}
 */
function removeFromWishlist(item) {
    pushEcommerce({
        event: 'eecEvent',
        eecAction: 'remove_from_wishlist',
        ecommerce: {
            items: [withListContext(item)],
        },
    });
}

/**
 * @returns {object}
 */
function readCartPayload() {
    const el = document.querySelector('[data-datalayer-cart]');

    if (!el) {
        return { items: [], value: 0, coupon: '' };
    }

    try {
        return JSON.parse(el.textContent);
    } catch (error) {
        return { items: [], value: 0, coupon: '' };
    }
}

/**
 * @returns {void}
 */
function beginCheckout() {
    const cart = readCartPayload();

    if (!cart.items.length) {
        return;
    }

    pushEcommerce({
        event: 'eecEvent',
        eecAction: 'begin_checkout',
        ecommerce: {
            currency: 'EUR',
            value: parseFloat(cart.value || 0),
            items: cart.items,
        },
    });
}

/**
 * @param {string} shippingTier
 * @param {number|null} value
 * @returns {void}
 */
function addShippingInfo(shippingTier, value = null) {
    const cart = readCartPayload();

    pushEcommerce({
        event: 'eecEvent',
        eecAction: 'add_shipping_info',
        ecommerce: {
            currency: 'EUR',
            value: parseFloat(value ?? cart.value ?? 0),
            coupon: cart.coupon || '',
            shipping_tier: shippingTier,
            items: cart.items,
        },
    });
}

/**
 * @param {string} paymentType
 * @returns {void}
 */
function addPaymentInfo(paymentType) {
    const cart = readCartPayload();

    pushEcommerce({
        event: 'eecEvent',
        eecAction: 'add_payment_info',
        ecommerce: {
            currency: 'EUR',
            value: parseFloat(cart.total_value ?? cart.value ?? 0),
            coupon: cart.coupon || '',
            payment_type: paymentType || '',
            items: cart.items,
        },
    });
}

/**
 * @returns {void}
 */
function viewCheckoutInfo() {
    const cart = readCartPayload();

    pushEcommerce({
        event: 'eecEvent',
        eecAction: 'view_checkout_info',
        ecommerce: {
            currency: 'EUR',
            value: parseFloat(cart.total_value ?? cart.value ?? 0),
            coupon: cart.coupon || '',
            items: cart.items,
        },
    });
}

/**
 * @returns {void}
 */
function beginPayment() {
    const cart = readCartPayload();

    pushEcommerce({
        event: 'eecEvent',
        eecAction: 'begin_payment',
        ecommerce: {
            currency: 'EUR',
            value: parseFloat(cart.total_value ?? cart.value ?? 0),
            coupon: cart.coupon || '',
            items: cart.items,
        },
    });
}

/**
 * @param {object} payload
 * @returns {void}
 */
function purchase(payload) {
    if (!payload || !payload.transaction_id) {
        return;
    }

    const storageKey = `datalayer_purchase_${payload.transaction_id}`;

    try {
        if (sessionStorage.getItem(storageKey)) {
            return;
        }

        sessionStorage.setItem(storageKey, '1');
    } catch (error) {
        // sessionStorage may be blocked; still push once this page load.
    }

    pushEcommerce({
        event: 'eecEvent',
        eecAction: 'purchase',
        ecommerce: payload,
    });
}

/**
 * @param {object} payload
 * @returns {void}
 */
function enhancedConversion(payload) {
    if (!payload || !hasDataLayer()) {
        return;
    }

    window.dataLayer.push({
        event: 'enhanced_conversion_data',
        enhanced_conversion_data: payload,
    });
}

/**
 * @param {object} payload
 * @returns {void}
 */
function viewForm(payload) {
    if (!payload) {
        return;
    }

    pushEcommerce({
        event: 'eecEvent',
        eecAction: 'view_form',
        ecommerce: payload,
    });
}

/**
 * @param {object} payload
 * @returns {void}
 */
function sentForm(payload) {
    if (!payload) {
        return;
    }

    pushEcommerce({
        event: 'eecEvent',
        eecAction: 'sent_form',
        ecommerce: payload,
    });
}

/**
 * @param {Element} script
 * @returns {object|null}
 */
function parseJsonScript(script) {
    try {
        return JSON.parse(script.textContent);
    } catch (error) {
        return null;
    }
}

let viewCartFired = false;

/**
 * @returns {void}
 */
function scanViewItemLists() {
    document.querySelectorAll('[data-datalayer-view-item-list]').forEach((script) => {
        if (script.getAttribute('data-datalayer-fired') === '1') {
            return;
        }

        const payload = parseJsonScript(script);

        if (!payload || !payload.items) {
            return;
        }

        script.setAttribute('data-datalayer-fired', '1');
        viewItemList(payload.items, payload.list_name || null, payload.list_id || null);
    });
}

/**
 * @returns {void}
 */
function scanViewItem() {
    document.querySelectorAll('[data-datalayer-view-item]').forEach((script) => {
        if (script.getAttribute('data-datalayer-fired') === '1') {
            return;
        }

        const payload = parseJsonScript(script);

        if (!payload) {
            return;
        }

        script.setAttribute('data-datalayer-fired', '1');
        viewItem(Array.isArray(payload) ? payload : [payload]);
    });
}

/**
 * @returns {void}
 */
function scanPurchase() {
    document.querySelectorAll('[data-datalayer-enhanced-conversion]').forEach((script) => {
        if (script.getAttribute('data-datalayer-fired') === '1') {
            return;
        }

        const payload = parseJsonScript(script);

        if (!payload) {
            return;
        }

        script.setAttribute('data-datalayer-fired', '1');
        enhancedConversion(payload);
    });

    document.querySelectorAll('[data-datalayer-purchase]').forEach((script) => {
        if (script.getAttribute('data-datalayer-fired') === '1') {
            return;
        }

        const payload = parseJsonScript(script);

        if (!payload) {
            return;
        }

        script.setAttribute('data-datalayer-fired', '1');
        purchase(payload);
    });
}

/**
 * @returns {void}
 */
function scanViewCart() {
    if (viewCartFired) {
        return;
    }

    const script = document.querySelector('[data-datalayer-view-cart]');

    if (!script) {
        return;
    }

    const payload = parseJsonScript(script);

    if (!payload || !payload.items || !payload.items.length) {
        return;
    }

    viewCartFired = true;
    viewCart(payload);
}

/**
 * @returns {void}
 */
function observeViewForms() {
    const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            if (!entry.isIntersecting || entry.intersectionRatio < 0.1) {
                return;
            }

            const target = entry.target;

            if (target.getAttribute('data-datalayer-view-form-fired') === '1') {
                observer.unobserve(target);

                return;
            }

            const raw = target.getAttribute('data-datalayer-view-form');

            if (!raw) {
                return;
            }

            try {
                viewForm(JSON.parse(raw));
                target.setAttribute('data-datalayer-view-form-fired', '1');
                observer.unobserve(target);
            } catch (error) {
                // Invalid JSON on the node; ignore.
            }
        });
    }, { threshold: [0.1] });

    document.querySelectorAll('[data-datalayer-view-form]').forEach((node) => {
        if (node.getAttribute('data-datalayer-view-form-fired') === '1') {
            return;
        }

        observer.observe(node);
    });
}

/**
 * @param {MouseEvent} event
 * @returns {void}
 */
function onProductCardClick(event) {
    const article = event.target.closest('[data-datalayer-item]');

    if (!article) {
        return;
    }

    const link = event.target.closest('a');

    if (!link || !article.contains(link)) {
        return;
    }

    try {
        selectItem(JSON.parse(article.getAttribute('data-datalayer-item')));
    } catch (error) {
        // Ignore malformed payloads.
    }
}

/**
 * @param {CustomEvent} event
 * @returns {*}
 */
function eventDetail(event, key) {
    const detail = event.detail;

    if (detail && typeof detail === 'object' && key in detail) {
        return detail[key];
    }

    if (Array.isArray(detail) && detail.length) {
        return key ? detail[0]?.[key] ?? detail[0] : detail[0];
    }

    return detail;
}

/**
 * @returns {void}
 */
function bindLivewireEvents() {
    window.addEventListener('datalayer-add-to-cart', (event) => {
        const item = eventDetail(event, 'item');

        if (item) {
            addToCart(item);
        }
    });

    window.addEventListener('datalayer-remove-from-cart', (event) => {
        const item = eventDetail(event, 'item');

        if (item) {
            removeFromCart(item);
        }
    });

    window.addEventListener('datalayer-add-to-wishlist', (event) => {
        const item = eventDetail(event, 'item');

        if (item) {
            addToWishlist(item);
        }
    });

    window.addEventListener('datalayer-remove-from-wishlist', (event) => {
        const item = eventDetail(event, 'item');

        if (item) {
            removeFromWishlist(item);
        }
    });

    window.addEventListener('datalayer-purchase', (event) => {
        purchase(eventDetail(event, 'payload'));
    });

    window.addEventListener('datalayer-enhanced-conversion', (event) => {
        enhancedConversion(eventDetail(event, 'payload'));
    });

    window.addEventListener('datalayer-sent-form', (event) => {
        sentForm(eventDetail(event, 'payload'));
    });
}

/**
 * @returns {void}
 */
function boot() {
    loadStandardData();
    scanViewItemLists();
    scanViewItem();
    scanViewCart();
    scanPurchase();
    observeViewForms();
    document.addEventListener('click', onProductCardClick, true);

    if (typeof Livewire !== 'undefined') {
        Livewire.hook('morph.updated', () => {
            scanViewItemLists();
            observeViewForms();
        });
    }
}

bindLivewireEvents();

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
} else {
    boot();
}

window.ecommerceDataLayer = {
    viewItemList,
    selectItem,
    viewItem,
    addToCart,
    removeFromCart,
    viewCart,
    addToWishlist,
    removeFromWishlist,
    beginCheckout,
    addShippingInfo,
    addPaymentInfo,
    viewCheckoutInfo,
    beginPayment,
    purchase,
    enhancedConversion,
    viewForm,
    sentForm,
    persistItemList,
    scanViewItemLists,
    readCartPayload,
};
